import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { principles, siteInfo, stats, voices } from "@/mocks/site";

const workflow = [
  { step: "01", title: "发现", desc: "从公开讨论中识别正在形成的热点，记录最早的可见时间点。" },
  { step: "02", title: "梳理", desc: "按时间顺序整理关键节点，标注每条进展的信息来源类型。" },
  { step: "03", title: "比对", desc: "把争议焦点拆成可验证的命题，逐条比对公开材料。" },
  { step: "04", title: "更新", desc: "出现新回应或新证据时更新页面，并保留最后更新时间。" },
];

export default function AboutPage() {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - 蝌蚪爆料",
      description: siteInfo.intro,
      url: "https://dfjor.cn/about",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      url: "https://dfjor.cn/",
      description: siteInfo.intro,
      email: siteInfo.email,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://dfjor.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://dfjor.cn/about" },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 - 蝌蚪爆料 | 品牌介绍与内容原则"
        description="蝌蚪爆料专注网络热议事件的进展追踪、争议梳理与辟谣核查。了解我们的网站定位、发展理念、内容原则、用户口碑与联系方式。"
        keywords="蝌蚪爆料,关于我们,内容原则,品牌介绍,网站定位"
        path="/about"
        jsonLd={structuredData}
      />

      <section className="border-b border-background-200 bg-background-100 px-4 py-10 md:px-6 md:py-12 lg:px-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
          关于蝌蚪爆料：只整理信息，不制造结论
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600">
          {siteInfo.intro}
        </p>
      </section>

      <section className="px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们是谁</h2>
            <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700">
              <p>
                蝌蚪爆料始于 {siteInfo.since} 年，是一个面向普通读者的热点事件追踪平台。
                我们不追逐最刺激的标题，也不急着替任何人下判断，而是尽量把一件事的
                <strong className="font-semibold text-foreground-950">来龙去脉按时间还原</strong>。
              </p>
              <p>
                对很多热点来说，读者真正需要的不是更多情绪，而是一条能看懂的时间线：
                事情从哪开始、中间发生了什么、当事人怎么回应、有没有被澄清的传言。
                把这些整理好，判断权仍然交回给读者。
              </p>
              <p>
                我们理解的「爆料」不是制造爆料，而是把散落各处的公开信息重新排序，
                让讨论回到事实上。
              </p>
            </div>

            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <dd className="font-heading text-2xl font-bold text-primary-600">{item.value}</dd>
                  <dt className="mt-1 text-xs text-foreground-600">{item.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          <div className="overflow-hidden rounded-lg border border-background-200">
            <div className="h-64 w-full bg-background-100 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20of%20a%20timeline%20and%20document%20pages%20arranged%20in%20order%20on%20cream%20paper%20texture%2C%20vermilion%20and%20amber%20accents%2C%20soft%20grain%2C%20calm%20organized%20mood%2C%20no%20text%2C%20no%20people&width=1000&height=800&seq=kb-about-01&orientation=landscape"
                alt="蝌蚪爆料整理事件时间线的工作示意插图"
                title="蝌蚪爆料 - 内容整理方式"
                className="h-full w-full object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          我们把自己定位成一张「热点事件的结构化索引」，而不是评论区。你在这里看到的是
          进展、争议、回应与核查，而不是谁的嗓门更大。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-flow-chart text-xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">进展可追溯</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              每个事件都有清晰时间线，隔几天再来也能快速追上进度。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-scales-3-line text-xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">争议看得懂</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              把吵架拆成几条具体分歧，帮助读者抓住真正的问题。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-shield-check-line text-xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">谣言可核对</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              逐条核查流传说法，信息不足时明确写「暂无定论」。
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展理念与内容原则</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          热度会过去，方法会留下。下面四条是我们每一次更新都尽量遵守的原则。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">一条内容是怎么来的</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          从发现到更新，通常会经过下面四步。步骤不复杂，但每一步都尽量留下痕迹。
        </p>
        <ol className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {workflow.map((item) => (
            <li key={item.step} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="font-heading text-2xl font-bold text-primary-300">{item.step}</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-4 py-12 md:px-6 md:py-16 lg:px-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">用户口碑</h2>
        <p className="mt-3 text-sm text-foreground-600">来自读者的一些反馈摘录（已获授权展示）。</p>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          {voices.map((voice) => (
            <figure key={voice.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">
                {voice.text}
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-2 border-t border-background-200 pt-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                  {voice.name.slice(0, 1)}
                </span>
                <span className="text-sm font-medium text-foreground-900">{voice.name}</span>
                <span className="text-xs text-foreground-500">· {voice.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="px-4 pb-14 md:px-6 md:pb-16 lg:px-8">
        <div className="flex flex-col gap-5 rounded-lg border border-background-200 bg-secondary-50 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              合作、纠错或线索提供，都可以通过下面的方式联系我们。
            </p>
            <p className="mt-3 text-sm text-foreground-700">
              业务联系：
              <a href={`mailto:${siteInfo.email}`} className="ml-1 font-medium text-primary-700 hover:text-primary-800">
                {siteInfo.email}
              </a>
            </p>
            <p className="mt-1 text-sm text-foreground-700">
              线索邮箱：
              <a href={`mailto:${siteInfo.tipEmail}`} className="ml-1 font-medium text-primary-700 hover:text-primary-800">
                {siteInfo.tipEmail}
              </a>
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
            前往联系与线索提交
          </Link>
        </div>
      </section>
    </>
  );
}